import { useEffect, useRef, useState } from 'react';
import { InlineLoading } from './ui.js';
import { useNavigate } from 'react-router-dom';
import { notificationLink } from '@technohull/shared';
import { useMarkNotificationRead, useNotifications, type AppNotification } from '../features/hooks.js';
import { IconBell } from './icons.js';
import { useBackToClose } from '../lib/media.js';
import { useDateFormatter } from '../lib/format.js';

// Where clicking a notification takes you: its own link (an order opens filtered to that order), else
// its event's page (shared with the emails' buttons). null = just mark it read, so a click is never a
// dead no-op that only closes the menu.
const destinationFor = (n: AppNotification): string | null => notificationLink(n);

export function NotificationsBell({ onNavigate }: {
  /** leave the open panel for a page; AppShell passes its overlay-aware navigate (see useLeaveOverlay). */
  onNavigate?: (path: string, close: () => void) => void;
} = {}) {
  const { data, isLoading, isError } = useNotifications();
  const mark = useMarkNotificationRead();
  const df = useDateFormatter(); // "5m ago" for a week, then the date in the user's format
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  useBackToClose(open, () => setOpen(false)); // touch: the back gesture closes the panel, not the page

  const unread = data?.unread ?? 0;
  const items = data?.notifications ?? [];

  // close the menu on Escape while it's open, handing focus back to the bell.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { setOpen(false); btnRef.current?.focus(); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const onClick = (n: AppNotification) => {
    if (!n.read_at) mark.mutate({ id: n.id });
    const dest = destinationFor(n);
    if (onNavigate) { onNavigate(dest ?? '', () => setOpen(false)); return; }
    setOpen(false);
    if (dest) navigate(dest);
  };

  return (
    <div style={{ position: 'relative' }}>
      <button
        ref={btnRef}
        className="bell-btn"
        onClick={() => setOpen((o) => !o)}
        aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
        aria-expanded={open}
        aria-controls="bell-menu"
        title="Notifications"
      >
        <IconBell size={18} />
        {unread > 0 && <span className="bell-badge">{unread > 9 ? '9+' : unread}</span>}
      </button>

      {open && (
        <>
          {/* click-catcher: invisible on desktop, a dim scrim behind the full-width phone panel */}
          <div className="bell-scrim" onClick={() => setOpen(false)} />
          {/* a disclosure panel, not an ARIA menu: it has no arrow-key model and mixes in non-item content */}
          <div className="bell-menu" id="bell-menu" role="region" aria-label="Notifications">
            <div className="bell-menu-hd">
              <span>Notifications</span>
              {unread > 0 && <button className="btn btn-sm btn-ghost" disabled={mark.isPending} onClick={() => mark.mutate({ all: true })}>Mark all read</button>}
            </div>
            {isLoading ? (
              <InlineLoading />
            ) : isError && !data ? (
              <div className="muted" style={{ padding: 16, textAlign: 'center' }}>Couldn't load notifications.</div>
            ) : (
              items.length === 0 && <div className="muted" style={{ padding: 16, textAlign: 'center' }}>No notifications.</div>
            )}
            {items.map((n) => (
              <button key={n.id} className={`bell-item ${n.read_at ? '' : 'unread'}`} onClick={() => onClick(n)}>
                <div className="bell-item-title">{n.title}</div>
                {n.message && <div className="bell-item-msg">{n.message}</div>}
                <div className="bell-item-time">{df.ago(n.created_at)}</div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
